import { useState, useCallback, useEffect, useRef } from 'react';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import { useDashboardStore } from '../store/useDashboardStore';
import useDashboardApi from './useDashboardApi';
import { logInfo } from '@lib/logger/logInfo';
import { logError } from '@lib/logger/logError';
import { getDateRangeFromPreset } from '../types/dashboard.types';
import type { ReportType } from '../components/ReportDownloadModal';
import type {
  DashboardSession,
  LoadingState,
  OrganizationInfo,
  ContactListType,
} from '../types/dashboard.types';

export interface UseDashboardViewModelReturn {
  // Data
  sessions: DashboardSession[];
  organizations: OrganizationInfo[];

  // Pagination
  totalSessions: number;
  totalPages: number;
  currentPage: number;
  rowsPerPage: number;
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;

  // Loading
  sessionsLoadingState: LoadingState;

  // User Context
  isSuperAdmin: boolean;

  // Actions
  downloadReport: (sessionId: string, type: ReportType, listType: ContactListType, organizationId?: string) => Promise<void>;
  downloadingSessionId: string | null;
}

export default function useDashboardViewModel (): UseDashboardViewModelReturn {
  const dashboardApi = useDashboardApi();
  const { currentUser } = useSessionStore();
  const {
    filters,
    currentPage,
    rowsPerPage,
    setCurrentPage: setStorePage,
    setRowsPerPage: setStoreRowsPerPage,
  } = useDashboardStore();

  // User context
  const isSuperAdmin = currentUser?.isSuperAdmin ?? false;
  const currentUserId = currentUser?.userId ?? '';

  // Data state
  const [sessions, setSessions] = useState<DashboardSession[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Organizations state (for filter dropdown)
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);

  // Loading states - Start with LOADING to show skeleton on initial load
  const [sessionsLoadingState, setSessionsLoadingState] = useState<LoadingState>('LOADING');
  const [downloadingSessionId, setDownloadingSessionId] = useState<string | null>(null);

  // Cache for organization names (loaded once, stored as ref to avoid re-render cascades)
  const orgNameMapRef = useRef<Map<string, string>>(new Map());
  const orgsLoadedRef = useRef(false);

  // Load organizations once on mount, then trigger initial session fetch
  useEffect(() => {
    const loadOrganizations = async (): Promise<void> => {
      const orgsResponse = await dashboardApi.getOrganizationsAsync();
      if (orgsResponse.success && orgsResponse.data !== undefined) {
        setOrganizations(orgsResponse.data);
        const nameMap = new Map<string, string>();
        orgsResponse.data.forEach((org) => {
          nameMap.set(org.id, org.name);
        });
        orgNameMapRef.current = nameMap;
        orgsLoadedRef.current = true;

        // Default to "Tendril" org if no org is selected
        const { filters: currentFilters, setFilters: updateFilters } = useDashboardStore.getState();
        if ((currentFilters.orgIds?.length ?? 0) === 0) {
          const tendrilOrg = orgsResponse.data.find(
            (org) => org.name.toLowerCase().includes('tendril'),
          );
          if (tendrilOrg !== undefined && currentUser?.isTendrilUser === true) {
            updateFilters({ orgIds: [tendrilOrg.id] });
          }
        }
      }
    };
    void loadOrganizations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch sessions
  const refreshSessions = useCallback(async () => {
    if (currentUserId === '') {
      return;
    }

    setSessionsLoadingState('LOADING');
    try {
      // Ensure date range is set from preset if not custom
      const effectiveFilters = { ...filters };

      if (effectiveFilters.dateRange?.from == null && effectiveFilters.dateRange?.preset !== 'custom') {
        const dateRange = getDateRangeFromPreset(effectiveFilters.dateRange?.preset ?? 'thisMonth');
        effectiveFilters.dateRange = {
          ...effectiveFilters.dateRange,
          from: dateRange.from,
          to: dateRange.to,
        };
      }

      // Use server-side pagination - backend handles org filtering for SUPER_ADMIN
      const response = await dashboardApi.getSessionsAsync(
        currentUserId,
        effectiveFilters,
        currentPage,
        rowsPerPage,
      );

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Unknown error');
      }

      let sessionsData = response.data?.data ?? [];

      if (sessionsData.length > 0) {
        // Ensure org names are available (handles race with mount-time org fetch)
        if (!orgsLoadedRef.current) {
          const orgsResponse = await dashboardApi.getOrganizationsAsync();
          if (orgsResponse.success && orgsResponse.data !== undefined) {
            setOrganizations(orgsResponse.data);
            const nameMap = new Map<string, string>();
            orgsResponse.data.forEach((org) => {
              nameMap.set(org.id, org.name);
            });
            orgNameMapRef.current = nameMap;
            orgsLoadedRef.current = true;
          }
        }

        // Fetch SDR names for the current page's sessions
        const sdrNameMap = new Map<string, string>();
        const uniqueSdrIds = Array.from(
          new Set(sessionsData.map((session) => session.sdrId).filter(Boolean))
        );

        if (uniqueSdrIds.length > 0) {
          const sdrsResponse = await dashboardApi.getSdrsAsync(
            currentUserId,
            uniqueSdrIds,
            null,
          );

          if (sdrsResponse.success && sdrsResponse.data.length > 0) {
            sdrsResponse.data.forEach((sdr) => {
              sdrNameMap.set(sdr.id, sdr.name);
            });
          }
        }

        // Map SDR and organization names to sessions
        sessionsData = sessionsData.map((session) => ({
          ...session,
          sdrName: sdrNameMap.get(session.sdrId) ?? undefined,
          organizationName: orgNameMapRef.current.get(session.organizationId) ?? undefined,
        }));
      }

      setSessions(sessionsData);

      // Use server-side pagination values
      setTotalSessions(response.totalCount ?? 0);
      setTotalPages(response.totalPages ?? 0);
      setSessionsLoadingState('SUCCESS');

      void logInfo('Dashboard sessions loaded.', 'DashboardViewModel.refreshSessions', {
        count: sessionsData.length,
        page: currentPage,
        totalCount: response.totalCount,
      });
    } catch (error) {
      setSessionsLoadingState('ERROR');
      void logError('Failed to load dashboard sessions.', 'DashboardViewModel.refreshSessions', { error }, true);
    }
  }, [currentUserId, filters, currentPage, rowsPerPage, dashboardApi]);

  // Pagination handlers
  const setCurrentPage = useCallback((page: number) => {
    setStorePage(page);
  }, [setStorePage]);

  const setRowsPerPage = useCallback((rows: number) => {
    setStoreRowsPerPage(rows);
  }, [setStoreRowsPerPage]);

  // Download report
  const downloadReport = useCallback(async (
    sessionId: string,
    type: ReportType,
    listType: ContactListType,
    organizationId?: string,
  ) => {
    setDownloadingSessionId(sessionId);
    try {
      if (type === 'original') {
        // TODO: Implement original CSV download
        // For now, fall back to compact
        await dashboardApi.downloadReportAsync(sessionId, 'compact', organizationId);
      } else {
        await dashboardApi.downloadReportAsync(
          sessionId,
          type,
          type === 'raw' ? organizationId : undefined
        );
      }
    } catch (error) {
      void logError('Failed to download report.', 'DashboardViewModel.downloadReport', { error, sessionId, type }, true);
    } finally {
      setDownloadingSessionId(null);
    }
  }, [dashboardApi]);

  // Auto-refresh on mount and filter changes.
  // NOTE: refreshSessions is safe to include as a dependency because dashboardApi
  // (from useDashboardApi) is referentially stable — all its methods use useCallback
  // with stable deps and the return object is wrapped in useMemo.
  useEffect(() => {
    if (currentUserId !== '') {
      void refreshSessions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, filters, currentPage, rowsPerPage]);

  return {
    // Data
    sessions,
    organizations,

    // Pagination
    totalSessions,
    totalPages,
    currentPage,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,

    // Loading
    sessionsLoadingState,

    // User Context
    isSuperAdmin,

    // Actions
    downloadReport,
    downloadingSessionId,
  };
}
